import type { Messages } from "../fr";

export const settings: Messages["settings"] = {
  page: {
    title: "Settings",
    documentTitle: "Settings — Hune",
    sections: {
      subscription: "Subscription",
      profile: "Profile",
      notifications: "Notifications",
      security: "Security",
    },
    vatNote: "Prices exclude VAT; VAT is calculated at checkout.",
  },
  plans: {
    names: { free: "Free", solo: "Solo", pro: "Pro" },
  },
  cadence: {
    named: {
      1: "every day",
      3: "twice a week",
      7: "every week",
      14: "every two weeks",
      30: "every month",
    },
    everyDays: (days: number) => `every ${days} days`,
  },
  offers: {
    titleOne: "My offer",
    titleMany: "My offers",
    documentTitleOne: "My offer — Hune",
    documentTitleMany: "My offers — Hune",
    loadSubject: "your offers",
    workspace: { before: "Offers of the client workspace ", after: " (read-only)." },
    empty:
      "No offer monitored yet. Add the address of your offer: the agent works out your target from it, then starts searching.",
    add: {
      title: "Add an offer",
      limitReached: (plan: string, maxOffers: number) =>
        `The ${plan} plan tracks ${maxOffers === 1 ? "1 offer" : `${maxOffers} offers`}, all in use: archive one or change plans.`,
      description: "The agent reads the page, works out the offer and its target, then starts monitoring.",
      urlLabel: "Address of the page that presents the offer",
      urlPlaceholder: "your-other-product.com",
      submit: "Add offer",
      failed: "Couldn’t add this offer.",
    },
    archive: {
      button: "Archive",
      confirm: (name: string) => `Archive “${name}”? Its monitoring stops, its leads stay.`,
      cancel: "Cancel",
    },
    card: {
      displayed: "Displayed",
      display: "Display",
      targeting: "Targeting",
      edit: "Edit",
      targetingPending: "The targeting will appear after the first pass.",
      watch: "Monitoring",
    },
    watchStatus: {
      active: (cadence: string) => `Monitoring ${cadence}`,
      paused: "Monitoring paused",
    },
  },
  profileForm: {
    name: "Offer name",
    decisionMaker: "Target decision-maker",
    summary: "Value proposition",
    summaryPlaceholder: "What the offer promises, to whom, instead of what.",
    segment: "Priority segment (ICP)",
    segmentPlaceholder: "The kind of organization to target first.",
    event: "Event that makes the need urgent",
    eventPlaceholder: "E.g. office move, hiring, switching tools…",
    exclusions: "Profiles to exclude",
    exclusionsPlaceholder: "One profile per line.",
    criteria: {
      legend: "Qualification criteria",
      help: "What the research looks for on each lead (size, tools in place, team…). Decisive criteria show first on the lead card.",
      label: "Criterion",
      labelPlaceholder: "E.g. Appointment booking tool",
      goodSign: "What counts as a good sign",
      goodSignPlaceholder: "Good sign, e.g. Calendly or phone only",
      remove: (label: string) => `Remove criterion ${label}`,
      searchTerms: "Search terms",
      searchTermsPlaceholder: "Search terms, separated by commas",
      decisive: "Decisive",
      removeShort: "Remove",
      add: "Add a criterion",
    },
    notes: "Instructions for the agent",
    notesPlaceholder: "Anything useful: region, team size, sectors to favor…",
    save: "Save",
    cancel: "Cancel",
    failed: "The update failed.",
    saved: "Profile saved: your next analyses will start from this version.",
  },
  profileView: {
    showLess: "Show less",
    readMore: "Read more",
    decisive: "decisive",
    rows: {
      summary: "Promise",
      segment: "Target",
      decisionMaker: "Decision-maker",
      triggers: "Triggers",
      exclusions: "Exclude",
      criteria: "Criteria",
      notes: "Instructions",
    },
  },
  watch: {
    cadenceOptions: {
      3: "Twice a week",
      7: "Weekly · recommended",
      14: "Every two weeks",
      30: "Monthly",
    },
    lockedSuffix: (plan: string) => ` · ${plan} plan`,
    switchLabel: (name: string) => `Automatic monitoring — ${name}`,
    switchText: "Automatic monitoring",
    cadence: "Frequency",
    saveFailed: "Couldn’t save the monitoring settings.",
    saved: "Monitoring saved: the agent will come back on its own.",
    paused: "Monitoring paused: no more automatic passes.",
    freePlanNote: "On the Free plan, the agent comes back once a month.",
    freePlanUpgrade: "More frequent monitoring",
    lastPass: (date: string) => `Last pass: ${date}.`,
    noPass: "No pass yet.",
    nextPass: (date: string) => `Next pass on ${date}.`,
    notCovered: "Not covered by your plan: the agent no longer runs passes on this offer.",
  },
  emailNotifications: {
    weeklyDigest: "Weekly email summary",
    weeklyDigestHelp: "Only when the agent has found something new, at most once a week.",
    failedWithCause: (message: string) => `${message} Your previous setting is kept.`,
    failed: "Couldn’t save: your previous setting is kept.",
    loadFailed: "Couldn’t load your email preferences. Reload the page.",
  },
  billing: {
    loadSubject: "your subscription",
    statuses: {
      active: "Active",
      trialing: "Trial",
      past_due: "Payment pending",
      unpaid: "Unpaid",
      canceled: "Canceled",
      incomplete: "Payment to complete",
    },
    cadence: {
      monthly: "Monthly",
      weekly: "Weekly",
      twiceWeekly: "2× / week",
    },
    adminAccount: "Admin account",
    free: "Free",
    pricePerYear: (price: string) => `${price} excl. VAT / year`,
    pricePerMonth: (price: string) => `${price} excl. VAT / month`,
    yourPlan: "Your plan",
    endsOn: (date: string) => `Ends on ${date}`,
    renewsOn: (date: string) => `Renews on ${date}`,
    trackedOffers: "Tracked offers",
    watch: "Monitoring",
    manualRuns: "On-demand analysis",
    unlimited: "Unlimited",
    oneFree: "1 free",
    included: "Included",
    research: "Research and PDF",
    planColumn: "Plan",
    perMonthUnit: "excl. VAT / month",
    currentPlan: "Current plan",
    choose: "Choose",
    thanks: "Thank you! Your subscription is active.",
    paymentReceived: "Payment received: your subscription will show up in a moment.",
    unavailable: "Payment is unavailable right now.",
    manage: "Subscription and invoices",
    compare: "Compare plans",
    interval: "Billing period",
    monthly: "Monthly",
    yearly: "Yearly · −20%",
    comingSoon: "Online payment coming soon: write to us to change plans.",
    manageHint: "Plan changes, card, invoices and cancellation: from “Subscription and invoices”.",
    seePlans: "See plans",
  },
  errors: {
    signIn: "Please log in.",
    signInToEditProfile: "Log in to edit your offer profile.",
    offerUrlRequired: "Enter the address of the offer to monitor.",
    offerUrlInvalid: "This address isn’t a public website.",
    addOfferFailed: "Couldn’t add this offer.",
    invalidOffer: "Invalid offer.",
    offerNotFound: "Offer not found.",
    saveOfferFailed: "Couldn’t save the offer.",
    invalidBody: "Invalid request body.",
    invalidField: (field: string) => `Invalid value for “${field}”.`,
    nothingToUpdate: "No field to update.",
    profileNotFound: "Offer profile not found.",
    invalidPreference: "Invalid preference.",
  },
};
